import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Locale = 'es' | 'en'

interface LocaleState {
  locale: Locale
  /**
   * Marcado a true cuando el usuario pulsó explícitamente el toggle de
   * idioma (anónimo o logueado). Falso por default. NO se persiste —
   * vive solo en memoria de la sesión.
   *
   * Resuelve el conflicto al login: si el localStorage tiene un valor
   * "explícito" del user (toggled=true), gana sobre el backend y el
   * `useLocaleSync` dispara un PATCH para alinear. Si no (toggled=false),
   * el backend gana y se sobreescribe el localeStore.
   */
  userToggled: boolean
  /** El user pulsó el toggle. Marca userToggled=true. */
  setLocale: (locale: Locale) => void
  /** Acepta el locale autoritativo del backend SIN marcar userToggled. */
  acceptBackendLocale: (locale: Locale) => void
  /** Tras un PATCH exitoso o un sync from backend, limpia el flag. */
  clearToggleFlag: () => void
  /**
   * Acepta el locale derivado de la URL (`/en/*`) sin marcar `userToggled`.
   * Distinto de `acceptBackendLocale`: la fuente es la URL del navegador
   * (vía `LocaleScope`), no un fetch al backend. Mantiene `userToggled`
   * intacto — la preferencia explícita del user no se sobrescribe.
   */
  acceptUrlLocale: (locale: Locale) => void
  /**
   * Idioma impuesto por la URL mientras se está en una ruta con idioma fijo (`LocaleScope`:
   * `/en/*`, ayuda y legales en español). Mientras no sea null, **la URL manda**: el idioma de
   * la cuenta (`useLocaleSync`) no lo pisa. No se persiste.
   */
  urlScope: Locale | null
  /** Entra en una ruta con idioma fijo: aplica su idioma y lo marca como impuesto por la URL. */
  enterUrlScope: (locale: Locale) => void
  /** Sale de esa ruta: el idioma se queda, pero deja de estar impuesto. */
  leaveUrlScope: () => void
}

const defaultLocale: Locale = (import.meta.env.VITE_DEFAULT_LOCALE as Locale) ?? 'es'

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      locale: defaultLocale,
      userToggled: false,
      setLocale: (locale) => set({ locale, userToggled: true }),
      acceptBackendLocale: (locale) => set({ locale, userToggled: false }),
      clearToggleFlag: () => set({ userToggled: false }),
      acceptUrlLocale: (locale) => set((state) => (state.locale === locale ? state : { locale })),
      urlScope: null,
      enterUrlScope: (locale) => set({ locale, urlScope: locale }),
      leaveUrlScope: () => set({ urlScope: null }),
    }),
    {
      name: 'matchplay.locale',
      // userToggled NO se persiste — debe arrancar siempre false en una
      // nueva sesión del browser. Si el user toggled en sesión previa pero
      // ya hubo PATCH al backend, no queremos volver a PATCHear.
      partialize: (state) => ({ locale: state.locale }),
    },
  ),
)
